import {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import {
  forceSimulation, forceManyBody, forceLink, forceCollide, forceX, forceY,
} from 'd3-force';
import { idKey, isSelfLoop, isIsolatedNode } from '../../lib/model';

const COLLIDE_PADDING = 10;
const CENTRE_STRENGTH = 0.02;

const round = (value) => Math.round(value * 100) / 100;

const stampNode = (simNode, node, edges, sizeOf) => {
  const size = sizeOf(node);
  simNode.radius = Math.max(size.width, size.height) / 2;
  simNode.isolated = isIsolatedNode(node.id, edges);
  simNode.fixed = node.physics === false || simNode.isolated;
  if (simNode.fixed) {
    if (!simNode.dragging) {
      simNode.fx = simNode.x;
      simNode.fy = simNode.y;
    }
  } else if (!simNode.dragging) {
    simNode.fx = null;
    simNode.fy = null;
  }
  return simNode;
};

/** Structure that should heat the layout. Isolated nodes are omitted so adding one is a no-op. */
export const linkedStructureKey = (nodes, edges) => {
  const nodeKey = nodes
    .filter((node) => !isIsolatedNode(node.id, edges))
    .map((node) => `${idKey(node.id)}${node.physics === false ? '!' : ''}`)
    .sort()
    .join('|');
  const edgeKey = edges
    .filter((edge) => !isSelfLoop(edge))
    .map((edge) => `${idKey(edge.from)}>${idKey(edge.to)}`)
    .sort()
    .join('|');
  return `${nodeKey}#${edgeKey}`;
};

/**
 * Optional force-directed layout ("Auto layout" in Settings). While enabled the simulation owns
 * node positions and reports them through `onTick`; `onEnd` fires when it settles. Isolated
 * nodes stay put and do not push others until they have a link to another node.
 */
export default function useForceLayout({
  enabled, nodes, edges, sizeOf, settings, onTick, onEnd,
}) {
  const simRef = useRef(null);
  const simNodesRef = useRef(new Map());
  const callbacksRef = useRef({ onTick, onEnd });
  useEffect(() => {
    callbacksRef.current = { onTick, onEnd };
  });

  const structureKey = useMemo(() => linkedStructureKey(nodes, edges), [nodes, edges]);

  const getPositions = useCallback(() => {
    const positions = {};
    simNodesRef.current.forEach((simNode, key) => {
      positions[key] = { x: round(simNode.x), y: round(simNode.y) };
    });
    return positions;
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (simRef.current) simRef.current.stop();
      simRef.current = null;
      simNodesRef.current = new Map();
      return undefined;
    }

    const previous = simNodesRef.current;
    const simNodes = nodes.map((node) => {
      const key = idKey(node.id);
      const existing = previous.get(key);
      return stampNode(existing || { id: key, x: node.x, y: node.y }, node, edges, sizeOf);
    });
    simNodesRef.current = new Map(simNodes.map((simNode) => [simNode.id, simNode]));

    const links = edges
      .filter((edge) => !isSelfLoop(edge)
        && simNodesRef.current.has(idKey(edge.from))
        && simNodesRef.current.has(idKey(edge.to)))
      .map((edge) => ({ source: idKey(edge.from), target: idKey(edge.to) }));

    const mobile = simNodes.filter((simNode) => !simNode.isolated);
    const sourced = mobile.length ? mobile : simNodes;
    const count = sourced.length || 1;
    const centre = sourced.reduce(
      (acc, simNode) => ({ x: acc.x + simNode.x / count, y: acc.y + simNode.y / count }),
      { x: 0, y: 0 },
    );

    const chargeOf = (simNode) => (simNode.isolated ? 0 : settings.charge);
    const sim = forceSimulation(simNodes)
      .force('charge', forceManyBody().strength(chargeOf))
      .force('link', forceLink(links)
        .id((d) => d.id)
        .distance(settings.linkDistance)
        .strength(settings.linkStrength))
      .force('collide', forceCollide().radius((d) => (
        d.isolated ? 0 : d.radius + COLLIDE_PADDING
      )))
      .force('x', forceX(centre.x).strength(CENTRE_STRENGTH))
      .force('y', forceY(centre.y).strength(CENTRE_STRENGTH))
      .on('tick', () => callbacksRef.current.onTick(getPositions()))
      .on('end', () => callbacksRef.current.onEnd?.());
    simRef.current = sim;

    return () => {
      sim.stop();
    };
    // Isolated add/remove is synced below without rebuilding.
    // `settings` changes are applied by the effect below without a rebuild.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, structureKey, sizeOf, getPositions]);

  // Slip isolated nodes in or out without reheating the running layout.
  useEffect(() => {
    const sim = simRef.current;
    if (!enabled || !sim) return;
    const live = sim.nodes();
    const have = new Set(live.map((simNode) => simNode.id));
    const wanted = new Map(nodes.map((node) => [idKey(node.id), node]));

    wanted.forEach((node, key) => {
      if (have.has(key)) return;
      live.push(stampNode({
        id: key, x: node.x, y: node.y, vx: 0, vy: 0,
      }, node, edges, sizeOf));
    });

    const next = live.filter((simNode) => wanted.has(simNode.id));
    if (next.length !== live.length) sim.nodes(next);
    simNodesRef.current = new Map(sim.nodes().map((simNode) => [simNode.id, simNode]));
  }, [enabled, nodes, edges, sizeOf]);

  useEffect(() => {
    const sim = simRef.current;
    if (!sim) return;
    sim.force('charge').strength((simNode) => (simNode.isolated ? 0 : settings.charge));
    sim.force('link').distance(settings.linkDistance).strength(settings.linkStrength);
    sim.alpha(0.6).restart();
  }, [settings.charge, settings.linkStrength, settings.linkDistance]);

  const dragStart = useCallback((id) => {
    const simNode = simNodesRef.current.get(idKey(id));
    const sim = simRef.current;
    if (!simNode || !sim) return;
    simNode.dragging = true;
    simNode.fx = simNode.x;
    simNode.fy = simNode.y;
    sim.alphaTarget(0.3).restart();
  }, []);

  const drag = useCallback((id, x, y) => {
    const simNode = simNodesRef.current.get(idKey(id));
    if (!simNode) return;
    simNode.fx = x;
    simNode.fy = y;
  }, []);

  const dragEnd = useCallback((id) => {
    const simNode = simNodesRef.current.get(idKey(id));
    const sim = simRef.current;
    if (!simNode || !sim) return;
    simNode.dragging = false;
    sim.alphaTarget(0);
    if (!simNode.fixed) {
      simNode.fx = null;
      simNode.fy = null;
    }
  }, []);

  return {
    dragStart, drag, dragEnd, getPositions,
  };
}
